/**
 * Dintass · leads
 *
 * 1. Si el tema tiene configurada una URL de webhook (settings.dintass_webhook_url), cada formulario
 *    marcado con data-dt-lead-form se envía también como JSON a esa URL antes del envío normal
 *    (que sigue llegando por correo vía el formulario 'contact' de Shopify).
 * 2. Publica eventos en window.dataLayer para GA4 / Meta: quote_request, whatsapp_click,
 *    cart_quote_open, quote_open, quote_abandoned.
 * 3. Borradores: lo que el cliente escribe en un formulario de cotización/contacto se guarda en el
 *    navegador (localStorage) y se vuelve a rellenar si vuelve sin haber enviado. Se borra al enviar.
 * 4. Leads parciales: si el cliente escribió correo o teléfono y se va sin enviar, el formulario se
 *    manda igual al webhook con origen "<origen>-parcial" y parcial: true (una vez por formulario),
 *    para poder contactarlo aunque no haya terminado.
 *
 * La URL se lee de <meta name="dintass-webhook" content="...">, impresa por layout/theme.liquid.
 */

const WEBHOOK_URL = document.querySelector('meta[name="dintass-webhook"]')?.content?.trim() || '';
const DRAFT_PREFIX = 'dt-lead-draft:';
const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 días
const CAMPOS_CONTACTO = ['email', 'phone'];
const CAMPOS_BORRADOR = ['name', 'email', 'phone', 'empresa', 'rut', 'region', 'tipo_cliente', 'body', 'cantidad'];

window.dataLayer = window.dataLayer || [];

function pushEvent(name, data = {}) {
  try {
    window.dataLayer.push({ event: name, ...data });
  } catch (_) {
    /* noop */
  }
}

/** contact[campo] → campo */
function nombreCampo(key) {
  const match = /^contact\[(.+)\]$/.exec(key);
  return match ? match[1] : key;
}

/**
 * Convierte contact[campo] → campo y arma un objeto plano.
 * @param {HTMLFormElement} form
 */
function formToLead(form) {
  const lead = {};
  for (const [key, value] of new FormData(form).entries()) {
    const name = nombreCampo(key);
    if (name === 'form_type' || name === 'utf8' || name === 'id') continue;
    lead[name] = typeof value === 'string' ? value : '';
  }
  lead.pagina = location.href;
  lead.enviado_en = new Date().toISOString();
  lead.tienda = location.host;
  return lead;
}

function sendWebhook(lead, { beacon = false } = {}) {
  if (!WEBHOOK_URL) return;
  try {
    const body = JSON.stringify(lead);
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(WEBHOOK_URL, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(WEBHOOK_URL, {
      method: 'POST',
      mode: 'no-cors',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
    }).catch((e) => console.warn('[dintass] webhook de leads falló', e));
  } catch (e) {
    console.warn('[dintass] webhook de leads falló', e);
  }
}

/* ------------------------------------------------------------------ borradores */

function claveBorrador(form) {
  return DRAFT_PREFIX + (form.getAttribute('data-dt-lead-form') || 'form');
}

function leerBorrador(form) {
  try {
    const raw = localStorage.getItem(claveBorrador(form));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || Date.now() - (data._t || 0) > DRAFT_TTL_MS) return null;
    return data;
  } catch (_) {
    return null;
  }
}

function guardarBorrador(form) {
  try {
    const data = { _t: Date.now() };
    let hay = false;
    for (const el of form.elements) {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) continue;
      const name = nombreCampo(el.name || '');
      if (!CAMPOS_BORRADOR.includes(name)) continue;
      if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio') && !el.checked) continue;
      if (el.value) { data[name] = el.value; hay = true; }
    }
    if (hay) localStorage.setItem(claveBorrador(form), JSON.stringify(data));
    else localStorage.removeItem(claveBorrador(form));
  } catch (_) {
    /* almacenamiento bloqueado: se ignora */
  }
}

function restaurarBorrador(form) {
  const data = leerBorrador(form);
  if (!data) return;
  for (const el of form.elements) {
    if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) continue;
    const name = nombreCampo(el.name || '');
    if (!(name in data) || el.value) continue;
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) { el.checked = el.value === data[name]; continue; }
    el.value = data[name];
  }
}

function borrarBorrador(form) {
  try { localStorage.removeItem(claveBorrador(form)); } catch (_) { /* noop */ }
}

/* ------------------------------------------------------------- leads parciales */

const formsTocados = new WeakSet();
const formsEnviados = new WeakSet();
const parcialEnviado = new WeakSet();

function tieneContacto(form) {
  const fd = new FormData(form);
  return CAMPOS_CONTACTO.some((c) => String(fd.get('contact[' + c + ']') || fd.get(c) || '').trim().length >= 6);
}

function enviarParcial(form, motivo) {
  if (formsEnviados.has(form) || parcialEnviado.has(form)) return;
  if (!formsTocados.has(form) || !tieneContacto(form)) return;
  parcialEnviado.add(form);
  const lead = formToLead(form);
  const origen = lead.origen || form.getAttribute('data-dt-lead-form') || 'web';
  lead.origen = origen + '-parcial';
  lead.parcial = true;
  lead.motivo = motivo;
  pushEvent('quote_abandoned', { origen, producto: lead.producto || 'carro', sku: lead.sku || '' });
  sendWebhook(lead, { beacon: true });
}

function leadForms() {
  return Array.from(document.querySelectorAll('form[data-dt-lead-form]'));
}

function initForm(form) {
  if (form.__dtLeadInit) return;
  form.__dtLeadInit = true;
  restaurarBorrador(form);
  form.addEventListener('input', () => { formsTocados.add(form); guardarBorrador(form); });
  form.addEventListener('change', () => { formsTocados.add(form); guardarBorrador(form); });
}

function initAll() {
  leadForms().forEach(initForm);
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-dt-lead-form')) return;
    formsEnviados.add(form);
    borrarBorrador(form);
    const lead = formToLead(form);
    lead.origen = lead.origen || form.getAttribute('data-dt-lead-form');
    pushEvent('quote_request', { origen: lead.origen, producto: lead.producto || 'carro', sku: lead.sku || '' });
    // keepalive permite que la petición sobreviva a la navegación del envío normal
    sendWebhook(lead);
  },
  true
);

// El cliente cierra la pestaña, cambia de página o deja la pestaña en segundo plano
window.addEventListener('pagehide', () => leadForms().forEach((f) => enviarParcial(f, 'pagehide')));
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') leadForms().forEach((f) => enviarParcial(f, 'hidden'));
});
// Cierre de un cajón de cotización (dialog) con datos escritos
document.addEventListener('close', (event) => {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement)) return;
  dialog.querySelectorAll('form[data-dt-lead-form]').forEach((f) => enviarParcial(f, 'cerrado'));
}, true);

document.addEventListener('click', (event) => {
  const el = event.target instanceof Element ? event.target.closest('[data-dt-event]') : null;
  if (!el) return;
  pushEvent(el.getAttribute('data-dt-event'), { producto: el.getAttribute('data-dt-product') || '' });
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
else initAll();
// Formularios que se renderizan después (drawers, secciones cargadas por JS)
new MutationObserver(() => initAll()).observe(document.documentElement, { childList: true, subtree: true });
